// API keys for the standalone mail product — S3-pure, no DynamoDB (v1).
//
// A mail customer authenticates the public send API with a bearer key. We store
// only the SHA-256 HASH of the key (never the plaintext) at
// `.s3p/api-keys/{hash}.json`; the plaintext is shown once at creation. Lookup =
// hash the presented key → GetObject that path. Email send isn't a sub-5ms path,
// so an S3 GET per request is fine; DynamoDB is deferred to hard-quota scale.
//
// Pure module (Web Crypto, no AWS) so it's usable in both the browser dashboard
// (create/revoke) and the Node runtime (verify), and easily unit-tested.

import { paths } from "./paths.ts";

export type ApiKeyEnv = "live" | "test";

export interface ApiKeyRecord {
	/** SHA-256 hex of the plaintext key — the storage id. */
	hash: string;
	/** Display prefix, e.g. `s3pmail_live_a1b2c3` (safe to show). */
	prefix: string;
	scopes: string[];
	createdAt: string;
	lastUsedAt?: string;
	revoked?: boolean;
	label?: string;
}

const PREFIX = "s3pmail";
const DISPLAY_PREFIX_LEN = 6; // chars of the random part kept for display

function randomToken(bytes = 24): string {
	const buf = new Uint8Array(bytes);
	globalThis.crypto.getRandomValues(buf);
	// base62-ish via base64url without padding.
	let bin = "";
	for (const b of buf) bin += String.fromCharCode(b);
	return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** SHA-256 hex of a string (the stored id for a key). */
export async function hashApiKey(key: string): Promise<string> {
	const data = new TextEncoder().encode(key);
	const digest = await globalThis.crypto.subtle.digest("SHA-256", data);
	return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Storage path for a key's record, by hash. */
export function apiKeyPath(hash: string): string {
	return paths().apiKey(hash);
}

/** Mint a new key. Returns the PLAINTEXT (show once) + the record to persist
 *  (hash only). `s3pmail_live_…` / `s3pmail_test_…`. */
export async function generateApiKey(
	env: ApiKeyEnv,
	opts: { scopes?: string[]; label?: string } = {},
): Promise<{ plaintext: string; record: ApiKeyRecord }> {
	const rand = randomToken();
	const plaintext = `${PREFIX}_${env}_${rand}`;
	const hash = await hashApiKey(plaintext);
	return {
		plaintext,
		record: {
			hash,
			prefix: `${PREFIX}_${env}_${rand.slice(0, DISPLAY_PREFIX_LEN)}`,
			scopes: opts.scopes ?? ["emails:send"],
			createdAt: new Date().toISOString(),
			...(opts.label ? { label: opts.label } : {}),
		},
	};
}

export function isTestKey(key: string): boolean {
	return key.startsWith(`${PREFIX}_test_`);
}

/** Validate a presented bearer key against its stored record. Returns the
 *  record if valid + not revoked + holds `requiredScope`; otherwise null. The
 *  caller supplies the lookup (S3 GetObject by `apiKeyPath(hash)`). */
export async function verifyApiKey(
	presented: string,
	getRecord: (hash: string) => Promise<ApiKeyRecord | null>,
	requiredScope = "emails:send",
): Promise<ApiKeyRecord | null> {
	if (!presented || !presented.startsWith(`${PREFIX}_`)) return null;
	const hash = await hashApiKey(presented);
	const rec = await getRecord(hash);
	if (!rec || rec.revoked) return null;
	if (requiredScope && !rec.scopes.includes(requiredScope)) return null;
	return rec;
}
